/** One bucket of an event's audience distribution, e.g. `{ bucket: '25-34', share: 0.3 }`. */
export type AudienceDistributionEntry = {
  bucket: string;
  /** 0-1 share of the event's estimated audience, as the coverage study reports it. */
  share: number;
};

export type AudienceGender = 'female' | 'male';

const GENDER_ALIASES: Record<string, AudienceGender> = {
  female: 'female',
  male: 'male',
  men: 'male',
  women: 'female'
};

/** The distribution's buckets, in the order the study reports them. `[]` when there is none. */
export function toDistributionEntries(distribution: Record<string, number> | null): AudienceDistributionEntry[] {
  return Object.entries(distribution ?? {}).map(([bucket, share]) => ({ bucket, share }));
}

/** Lower bound of an age bucket ("25-34" -> 25, "65+" -> 65); unparsable buckets sort last. */
function getAgeBucketStart(bucket: string): number {
  const start = Number.parseInt(bucket, 10);
  return Number.isNaN(start) ? Number.POSITIVE_INFINITY : start;
}

/** Age buckets from youngest to oldest. */
export function sortAgeEntries(entries: AudienceDistributionEntry[]): AudienceDistributionEntry[] {
  return [...entries].sort((a, b) => getAgeBucketStart(a.bucket) - getAgeBucketStart(b.bucket));
}

/** Normalized gender of a gender bucket ("women" -> "female"); null when it isn't a known one. */
export function getAudienceGender(bucket: string): AudienceGender | null {
  return GENDER_ALIASES[bucket.toLowerCase()] ?? null;
}
